import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';

const Field = ({
  icon: Icon,
  label,
  required,
  error,
  helperText,
  type = 'text',
  as = 'input',
  containerStyle,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(!!props.value || !!props.defaultValue);

  const handleChange = (e) => {
    setHasValue(!!e.target.value);
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const InputComponent = as === 'textarea' ? 'textarea' : as === 'select' ? 'select' : 'input';

  return (
    <div style={{ marginBottom: '12px', ...containerStyle }}>
      {/* Label */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          fontSize: '11px',
          fontWeight: 600,
          color: error
            ? 'rgba(239, 68, 68, 0.9)'
            : isFocused
            ? 'rgba(30, 123, 52, 0.9)'
            : 'rgba(255, 255, 255, 0.65)',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          transition: 'color 0.2s ease',
        }}
      >
        {Icon && (
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '3px',
              background: error
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.3) 100%)'
                : isFocused
                ? 'linear-gradient(135deg, #1E7B34 0%, #145A25 30%, #0F4A1F 70%, #0A3A18 100%)'
                : 'linear-gradient(135deg, rgba(30, 123, 52, 0.3) 0%, rgba(20, 90, 37, 0.5) 100%)',
              border: isFocused ? '0.5px solid rgba(255, 255, 255, 0.15)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isFocused
                ? '1px 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(0, 0, 0, 0.1)'
                : '0 1px 3px rgba(0,0,0,0.3)',
              transition: 'all 0.2s ease',
              transform: isFocused ? 'scale(1.05)' : 'scale(1)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Periodic table highlights on icon when focused */}
            {isFocused && !error && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    top: '1px',
                    left: '1px',
                    right: '1px',
                    height: '8px',
                    borderRadius: '2px 2px 0 0',
                    background: 'linear-gradient(to bottom, rgba(129, 199, 132, 0.7) 0%, rgba(102, 187, 106, 0.3) 40%, transparent 100%)',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '1px',
                    left: '1px',
                    width: '8px',
                    bottom: '1px',
                    borderRadius: '2px 0 0 2px',
                    background: 'linear-gradient(to right, rgba(129, 199, 132, 0.6) 0%, rgba(102, 187, 106, 0.2) 40%, transparent 100%)',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />
              </>
            )}
            <Icon
              size={12}
              style={{
                color: error ? '#EF4444' : isFocused ? '#ffffff' : '#66BB6A',
                transition: 'color 0.2s ease',
                position: 'relative',
                zIndex: 2,
              }}
            />
          </div>
        )}
        <span>{label}</span>
        {required && (
          <span
            style={{
              color: '#EF4444',
              marginLeft: '2px',
              fontSize: '10px',
            }}
          >
            *
          </span>
        )}
      </label>

      {/* Input Container - Periodic Table Style */}
      <div
        style={{
          position: 'relative',
          transition: 'all 0.2s ease',
        }}
      >
        <InputComponent
          className="input-field"
          type={as === 'input' ? type : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          style={{
            width: '100%',
            height: as === 'textarea' ? 'auto' : '44px',
            minHeight: as === 'textarea' ? '80px' : '44px',
            padding: as === 'textarea' ? '12px 14px' : '0 14px',
            background: error
              ? 'linear-gradient(145deg, rgba(20, 0, 0, 0.7) 0%, rgba(15, 0, 0, 0.9) 100%)'
              : isFocused
              ? 'linear-gradient(135deg, #1E7B34 0%, #145A25 30%, #0F4A1F 70%, #0A3A18 100%)'
              : 'linear-gradient(145deg, rgba(0, 0, 0, 0.6) 0%, rgba(10, 10, 10, 0.8) 100%)',
            border: error
              ? '1.5px solid rgba(239, 68, 68, 0.6)'
              : isFocused
              ? '0.5px solid rgba(255, 255, 255, 0.2)'
              : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            color: isFocused ? '#ffffff' : 'var(--text-primary)',
            fontFamily: 'Roboto Condensed, sans-serif',
            fontSize: '13px',
            fontWeight: 500,
            letterSpacing: '0.3px',
            lineHeight: as === 'textarea' ? '1.6' : '1.5',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            zIndex: 3,
            boxShadow: error
              ? 'inset 2px 2px 8px rgba(0,0,0,0.6), 0 0 0 3px rgba(239, 68, 68, 0.1), 0 2px 4px rgba(0,0,0,0.2)'
              : isFocused
              ? '1px 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(0, 0, 0, 0.1)'
              : 'inset 2px 2px 6px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.1)',
            resize: as === 'textarea' ? 'vertical' : undefined,
            overflow: 'hidden',
            ...props.style,
          }}
          {...props}
        />

        {/* Periodic Table Top Highlight - only when focused */}
        {isFocused && !error && (
          <div
            style={{
              position: 'absolute',
              top: '1px',
              left: '1px',
              right: '1px',
              height: '14px',
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(to bottom, rgba(129, 199, 132, 0.7) 0%, rgba(102, 187, 106, 0.3) 40%, transparent 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}

        {/* Periodic Table Left Highlight - only when focused */}
        {isFocused && !error && (
          <div
            style={{
              position: 'absolute',
              top: '1px',
              left: '1px',
              width: '14px',
              bottom: '1px',
              borderRadius: '3px 0 0 3px',
              background: 'linear-gradient(to right, rgba(129, 199, 132, 0.6) 0%, rgba(102, 187, 106, 0.2) 40%, transparent 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}

        {/* Periodic Table Bottom Shadow - only when focused */}
        {isFocused && !error && (
          <div
            style={{
              position: 'absolute',
              bottom: '1px',
              left: '1px',
              right: '1px',
              height: '10px',
              borderRadius: '0 0 3px 3px',
              background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0.25) 100%)',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}

        {/* Periodic Table Right Shadow - only when focused */}
        {isFocused && !error && (
          <div
            style={{
              position: 'absolute',
              top: '1px',
              right: '1px',
              width: '10px',
              bottom: '1px',
              borderRadius: '0 3px 3px 0',
              background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.08) 60%, rgba(0, 0, 0, 0.2) 100%)',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}
      </div>

      {/* Helper Text or Error */}
      {(error || helperText) && (
        <div
          style={{
            marginTop: '6px',
            fontSize: '11px',
            color: error ? '#EF4444' : 'rgba(255, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
            lineHeight: 1.4,
            animation: error ? 'slideUp 0.2s ease' : undefined,
          }}
        >
          {error && <AlertCircle size={12} style={{ flexShrink: 0, marginTop: '1px' }} />}
          <span>{error || helperText}</span>
        </div>
      )}
    </div>
  );
};

export default Field;
